/*
 * Copyright 2024 NewMedia Centre - Delft University of Technology
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

'use client'

// import dependencies
import { useState, useCallback } from 'react'
import { useTags } from '@/hooks/tags'

// import components
import Icon from '@mdi/react'
import { ReactTags } from 'react-tag-autocomplete'
import {
	Checkbox,
	Combobox,
	ComboboxInput,
	ComboboxOptions,
	ComboboxOption,
	Listbox,
	ListboxButton,
	ListboxOption,
	ListboxOptions,
	Input as InputBase,
	Textarea as TextareaBase,
} from '@headlessui/react'

// import icons
import {
	mdiClose,
	mdiChevronDown,
	mdiCheck
} from '@mdi/js'

// import types
import type { Tag } from 'react-tag-autocomplete'
import type { CheckboxProps, InputProps, TextareaProps } from '@headlessui/react'

export const Input = (props: Omit<InputProps, 'className'>) => <InputBase {...props} className='w-full rounded border border-800 bg-black px-3 py-2 outline-none placeholder:italic placeholder:text-400' />
export const Textarea = (props: TextareaProps) => <TextareaBase {...props} className={'w-full rounded border border-800 bg-black px-3 py-2 outline-none placeholder:italic placeholder:text-400 ' + props.className} />
export const Toggle = (props: CheckboxProps) => <Checkbox {...props} className='group flex size-8 cursor-pointer items-center justify-center rounded border border-800 bg-black'>
	<Icon path={mdiCheck} size={0.75} className='opacity-0 group-data-[checked]:opacity-100' />
</Checkbox>

export type TagInputProps = {
	values: string[]
	disabled?: boolean
	onUpdate: (values: string[]) => void
}

export const TagInput = ({ values, disabled, onUpdate }: TagInputProps) => {
	const tags = useTags()

	const onAdd = useCallback((tag: Tag) => onUpdate([...values, tag.label.toLowerCase()]), [values, onUpdate])
	const onDelete = useCallback((i: number) => onUpdate(values.filter((_, index) => index !== i)), [values, onUpdate])

	return (
		<ReactTags
			labelText=''
			selected={values.map(x => ({ label: x, value: x }))}
			suggestions={tags.map(x => ({ label: x, value: x }))}
			onAdd={onAdd}
			onDelete={onDelete}
			allowNew
			allowBackspace={false}
			isDisabled={disabled}
			placeholderText='Add tag...'
			renderHighlight={({ text }) => <span className='font-semibold'>{text}</span>}
			renderLabel={() => <></>}
			renderTagList={({ children }) => <>{children}</>}
			renderTag={({ classNames, tag, ...props }) => <button disabled={disabled} className='flex h-8 items-center justify-center gap-2 rounded-sm bg-700 px-2 text-xs capitalize enabled:hover:bg-600' {...props}><span>{tag.label}</span><Icon path={mdiClose} size={0.5} className='text-400' /></button>} // eslint-disable-line @typescript-eslint/no-unused-vars
			renderInput={({ classNames, inputWidth, ...props }) => <input readOnly={disabled} className='h-8 w-full bg-transparent ps-1 outline-none placeholder:italic placeholder:text-400 read-only:placeholder:text-transparent' {...props} />} // eslint-disable-line @typescript-eslint/no-unused-vars
			classNames={{
				root: 'relative flex flex-wrap w-full gap-1 rounded border border-800 bg-black p-1 outline-none',
				rootIsActive: '',
				rootIsDisabled: '',
				rootIsInvalid: '',
				label: '',
				tagList: '',
				tagListItem: '',
				tag: '',
				tagName: '',
				comboBox: 'grow',
				input: '',
				listBox: 'absolute z-50 left-0 mt-3 max-h-48 w-full overflow-y-auto rounded border border-700 bg-900',
				option: 'px-2 py-1 cursor-pointer hover:bg-700 aria-selected:text-400 capitalize',
				optionIsActive: '',
				highlight: '',
			}}
		/>
	)
}


export const TagsFilter = ({ values, onUpdate }: TagInputProps) => {
	const tags = useTags()

	const onAdd = useCallback((tag: Tag) => onUpdate([...values, tag.label.toLowerCase()]), [values, onUpdate])
	const onDelete = useCallback((i: number) => onUpdate(values.filter((_, index) => index !== i)), [values, onUpdate])

	return (
		<ReactTags
			labelText=''
			selected={values.map(x => ({ label: x, value: x }))}
			suggestions={tags.map(x => ({ label: x, value: x }))}
			onAdd={onAdd}
			onDelete={onDelete}
			allowBackspace
			placeholderText={values.length > 0 ? '' : 'Filter by tags...'}
			renderHighlight={({ text }) => <span className='font-semibold'>{text}</span>}
			renderLabel={() => <></>}
			renderTagList={({ children }) => <>{children}</>}
			renderTag={({ classNames, tag, ...props }) => <button className='flex h-8 items-center justify-center gap-2 rounded-sm bg-700 px-2 text-xs capitalize enabled:hover:bg-600' {...props}><span>{tag.label}</span><Icon path={mdiClose} size={0.5} className='text-400' /></button>} // eslint-disable-line @typescript-eslint/no-unused-vars
			renderInput={({ classNames, inputWidth, ...props }) => <input className='h-8 w-full bg-transparent ps-1 outline-none placeholder:text-400' {...props} />} // eslint-disable-line @typescript-eslint/no-unused-vars
			classNames={{
				root: 'relative flex flex-wrap w-full gap-1 outline-none',
				rootIsActive: '',
				rootIsDisabled: '',
				rootIsInvalid: '',
				label: '',
				tagList: '',
				tagListItem: '',
				tag: '',
				tagName: '',
				comboBox: 'grow',
				input: '',
				listBox: 'absolute z-50 left-0 mt-3 max-h-48 w-full overflow-y-auto rounded border border-700 bg-900',
				option: 'px-2 py-1 cursor-pointer hover:bg-700 aria-selected:text-400 capitalize',
				optionIsActive: '',
				highlight: '',
			}}
		/>
	)
}

export type AutoCompleteProps<T> = {
	value: T | null
	collection: T[]
	placeholder?: string
	onChange?: (value: T | null) => void
	display?: (value: T | null) => string
	filter?: (value: T, query: string) => boolean
	id?: (value: T | null) => string
}

export const AutoComplete = <T,>({ value, collection, placeholder, onChange, display, filter, id }: AutoCompleteProps<T>) => {
	const [query, setQuery] = useState('')

	const filtered = (query === '') ? collection : collection.filter(x => filter ? filter(x, query) : '')

	return (
		<Combobox immediate value={value} by={(a, b) => (id ? id(a) : '') === (id ? id(b) : '')} onChange={onChange}>
			<div className='relative w-full'>
				<ComboboxInput onChange={e => setQuery(e.target.value)} displayValue={x => (x && display) ? display(x as T) : ''} className='w-full rounded border border-800 bg-black px-3 py-2 outline-none placeholder:italic placeholder:text-400' placeholder={placeholder} />
				<ComboboxOptions className='absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded border border-800 bg-black'>
					{filtered.map(x => (
						<ComboboxOption key={id ? id(x) : ''} value={x} className='cursor-pointer px-3 py-2 hover:bg-900'>
							{display ? display(x) : ''}
						</ComboboxOption>
					))}
				</ComboboxOptions>
			</div>
		</Combobox>
	)
}

export type MultiAutoCompleteProps<T> = {
	value: T[]
	collection: T[]
	placeholder?: string
	onChange?: (value: T[] | null) => void
	display?: (value: T) => string
	filter?: (value: T, query: string) => boolean
	id?: (value: T) => string
}

export const MultiAutoComplete = <T,>({ value, collection, placeholder, onChange, display, filter, id }: MultiAutoCompleteProps<T>) => {
	const [query, setQuery] = useState('')

	const filtered = (query === '') ? collection : collection.filter(x => filter ? filter(x, query) : '')

	return (
		<Combobox immediate multiple value={value} by={(a, b) => (id ? id(a) : '') === (id ? id(b) : '')} onChange={onChange}>
			<div className='relative w-full'>
				<ComboboxInput onChange={e => setQuery(e.target.value)} displayValue={(x: T[]) => x.map(y => (y && display) ? display(y) : '').join(', ')} className='w-full rounded border border-200 bg-white px-3 py-2 outline-none placeholder:italic placeholder:text-400' placeholder={placeholder} />
				<ComboboxOptions className='absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded border border-200 bg-white'>
					{filtered.map(x => (
						<ComboboxOption key={id ? id(x) : ''} value={x} className='cursor-pointer px-3 py-2 hover:bg-100'>
							{display ? display(x) : ''}
						</ComboboxOption>
					))}
				</ComboboxOptions>
			</div>
		</Combobox>
	)
}

export type SelectProps<T> = {
	value: T
	collection: T[]
	onChange: (value: T | null) => void
	display?: (value: T) => string
	id?: (value: T) => string
	disabled?: boolean
}

export const Select = <T,>({ value, collection, onChange, display, id, disabled }: SelectProps<T>) => {
	return (
		<Listbox value={value} onChange={x => onChange(x)} disabled={disabled ?? false}>
			<div className='relative w-full'>
				<ListboxButton className='w-full rounded border border-800 bg-black px-3 py-2 text-left outline-none placeholder:italic placeholder:text-400 disabled:text-400'>
					{display ? display(value) : value as string}
				</ListboxButton>
				<ListboxOptions className='absolute left-0 z-50 mt-2 max-h-48 w-full overflow-y-auto rounded border border-700 bg-900'>
					{collection.map(x => (
						<ListboxOption key={id ? id(x) : x as string} value={x} className='flex cursor-pointer items-center justify-between px-2 outline-none hover:bg-700'>
							{({ selected }) => (
								<>
									<span className='py-1'>{display ? display(x) : x as string}</span>
									{selected && (
										<Icon path={mdiCheck} size={0.75} />
									)}
								</>
							)}
						</ListboxOption>
					))}
				</ListboxOptions>
			</div>
		</Listbox>
	)
}

export type MultiSelectProps<T> = {
	value: T[]
	collection: T[]
	label: string
	onChange: (value: T[]) => void
	display?: (value: T) => string
	id?: (value: T) => string
}

export const MultiSelect = <T,>({ value, collection, label, onChange, display, id }: MultiSelectProps<T>) => {
	return (
		<Listbox value={value} onChange={onChange} multiple>
			<div className='relative w-full overflow-visible'>
				<ListboxButton className='flex h-10 w-full items-center justify-between rounded px-3 text-left font-semibold outline-none hover:bg-700'>
					<span>{label}</span>
					<Icon path={mdiChevronDown} size={1} />
				</ListboxButton>
				<ListboxOptions className='absolute left-0 z-50 mt-2 max-h-48 w-full overflow-y-auto rounded border border-700 bg-900'>
					{collection.map(x => (
						<ListboxOption key={id ? id(x) : x as string} value={x} className='flex cursor-pointer items-center justify-between px-2 outline-none hover:bg-700'>
							{({ selected }) => (
								<>
									<span className='py-1'>{display ? display(x) : x as string}</span>
									{selected && (
										<Icon path={mdiCheck} size={0.75} />
									)}
								</>
							)}
						</ListboxOption>
					))}
				</ListboxOptions>
			</div>
		</Listbox>
	)
}